import { makeStyles } from '@material-ui/styles'
import { keys, prop } from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { ControlPlaneServicesProvider } from 'app/plugins/infrastructure/components/common/service-health/ControlPlaneServicesContext'
import { listRegions } from 'app/plugins/infrastructure/components/regions/actions'
import { regionsSelector } from 'app/plugins/infrastructure/components/regions/selectors'
import { EnrollmentFeature } from 'app/plugins/openstack/components/infrastructure/hosts/enroll-hosts/EnrollmentTray'
import { VMHAProvider } from 'app/plugins/openstack/components/infrastructure/hosts/vmha'
import { VolumeBackendsProvider } from 'app/plugins/openstack/components/storage/volume-types/VolumeBackendsProvider'
import { RevertVmFeature } from 'app/plugins/openstack/components/vms/revert-runs/RevertVmTray'
import type { RootState } from 'app/store'
import { initiateChameleon, setAmplitudeUser, setChameleonUser } from 'app/utils/tracking'
import DocumentMeta from 'core/components/DocumentMeta'
import { NotificationBarOffsetRoot } from 'core/components/notification-bar/NotificationBarOffsetContext'
import { MessageTypes } from 'core/components/notifications/model'
import Progress from 'core/components/progress/Progress'
import SessionExpiryModal from 'core/components/SessionExpiryModal'
import { determineCurrentStack, loadRegionFeatures } from 'core/containers/AuthController'
import { IsoMountProgressProvider } from 'core/context/isoMountProgressContext'
import { ModalProvider } from 'core/context/ModalContext'
import { SupportBundleProgressProvider } from 'core/context/supportBundleProgressContext'
import { UploadProgressProvider } from 'core/context/uploadFileProgressContext'
import RedirectToSecondaryRegion from 'core/elements/header/GlobalRegionDropdown'
import useListAction from 'core/hooks/useListAction'
import pluginManager from 'core/plugins/pluginManager'
import { useToast } from 'core/providers/ToastProvider'
import { type PreferencesState, preferencesStoreKey } from 'core/session/preferencesReducers'
import { type SessionState, sessionActions, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { getRegionTerm, isInfraRegion, isOpenCloud } from 'core/utils/helpers'
import Watchdog from 'core/watchdog'
import sessionTimeoutCheck from 'core/watchdog/session-timeout'
import { getBlueprints, getHostNetworkConfigs } from 'openstack/components/infrastructure/actions'
import Bugsnag from 'utils/bugsnag'
import AppBanners from './AppBanners'

// AUTHC-FR-001: Underlay network summary for the session: underlay type, VNI ranges ("a:b" becomes
// "a-b", "a:a" becomes "a") and the network label mapped to the first host's tunneling interface
export const getUnderlayInfo = (blueprintSettings, hostConfigs) => {
  const underlayType = blueprintSettings?.virtualNetworking?.underlayType
  const vnidRange = blueprintSettings?.virtualNetworking?.vnidRange || ''
  const vniRanges = vnidRange
    .split(',')
    .map((range) => range.trim())
    .map((range) => {
      const parts = range.split(':')
      return parts[0] === parts[1] ? parts[1] : `${parts[0]}-${parts[1]}`
    })
  const firstHostConfig = hostConfigs?.[0]
  const tunnelPhysnet = firstHostConfig?.tunnelingInterface
  const labelKeys = keys(firstHostConfig?.networkLabels)
  const tunnelLabel = labelKeys?.find((key) => {
    return firstHostConfig?.networkLabels?.[key] === tunnelPhysnet
  })
  return {
    type: underlayType,
    label: tunnelLabel,
    ranges: vniRanges,
  }
}

// See AuthContainer.spec.md for AUTHC-FR-002, AUTHC-FR-003, AUTHC-FR-004, AUTHC-FR-005,
// AUTHC-FR-006, AUTHC-FR-007, AUTHC-FR-008, AUTHC-FR-009 and AUTHC-FR-010
const AuthContainer = () => {
  const classes = useStyles()
  const { history, location } = useReactRouter()
  const dispatch = useDispatch()
  const showToast = useToast()
  const { prefs, updatePrefs } = useScopedPreferences()
  const { lastStack } = prefs
  const isOpenCloudProduct = isOpenCloud()
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))

  const {
    userDetails: { id: userId, name, displayName, role },
    features,
    onboardingRedirectToUrl,
    activeRegion,
    loadingRegion,
  } = session
  // stack is the name of the plugin (ex. kubernetes, account, developer, theme)
  const [currentStack, setStack] = useState(
    determineCurrentStack(history.location, lastStack, isOpenCloudProduct),
  )
  const { global = {} } = useSelector(prop<string, PreferencesState>(preferencesStoreKey))
  const { featureFlags: globalFeatureFlags } = global
  const plugins = pluginManager.getPlugins()
  const currentPlugin = plugins[currentStack]

  const PluginFrame = currentPlugin.getFrame()

  // Load regions list - only after authentication is established
  const { loading: loadingRegionList, reload: reloadRegions } = useListAction(listRegions, {
    initialLoadingState: true,
    loadOnDemand: true, // Don't load automatically
  })
  const regionList = useSelector(regionsSelector)

  // Ensure userDetails is loaded before rendering child components
  // This guarantees isAdmin() and other session-dependent checks work correctly
  const isUserDetailsLoaded = useMemo(() => {
    return userId && role
  }, [userId, role])

  // Load regions only after authentication is established
  useEffect(() => {
    if (isUserDetailsLoaded && !regionList?.length) {
      reloadRegions()
    }
  }, [isUserDetailsLoaded, regionList, reloadRegions])

  // When only Infra region is available
  const isSingleRegionDeployment = useMemo(() => regionList?.length === 1, [regionList])

  useEffect(() => {
    // targetPath is set in LoginPage.tsx and GlobalRegionDropdown.tsx.
    // This is used to redirect the user to the intended destination after login.
    const target = sessionStorage.getItem('targetPath')
    if (target) {
      sessionStorage.removeItem('targetPath')
      history.push(target)
    }
  }, [])

  const isLoadingInitialData = false
  const showOnboarding = false
  const regionTerm = getRegionTerm()

  useEffect(() => {
    async function loadBlueprintSettings() {
      // getBlueprints() hits an admin/infra-restricted resmgr endpoint that 403s for
      // non-admin roles (e.g. legacy self-service-user, PCD-9034) — skip the call
      // entirely for them rather than firing a request we know will be rejected.
      if (activeRegion && isAdmin()) {
        let blueprints
        try {
          blueprints = await getBlueprints()
        } catch (err) {
          console.error('Failed to load blueprint settings', err)
          return
        }
        const blueprintSettings = blueprints?.[0]
        if (!blueprintSettings) {
          return
        }
        const virtualNetworking = !!blueprintSettings?.virtualNetworking?.enabled
        const hostConfigs = await getHostNetworkConfigs()
        const underlayNetwork = getUnderlayInfo(blueprintSettings, hostConfigs)
        dispatch(
          sessionActions.updateSession({
            virtualNetworking,
            underlayNetwork,
          }),
        )
      }
    }
    loadBlueprintSettings()
  }, [activeRegion, features])

  useEffect(() => {
    if (showOnboarding) return

    if (onboardingRedirectToUrl) {
      history.push(onboardingRedirectToUrl)
      dispatch(sessionActions.updateSession({ onboardingRedirectToUrl: null }))
    }
  }, [showOnboarding, globalFeatureFlags, onboardingRedirectToUrl, history])

  // Load features once on startup, other times will be handled by region selection
  useEffect(() => {
    const loadRegion = async () => {
      try {
        await loadRegionFeatures()
      } finally {
        dispatch(sessionActions.updateSession({ loadingRegion: false }))
      }
    }
    loadRegion()
  }, [])

  useEffect(() => {
    if (!location) {
      return
    }
    const newStack = determineCurrentStack(location, lastStack, isOpenCloudProduct)
    setStack(newStack)
    updatePrefs({ lastStack: newStack })
  }, [location, isOpenCloudProduct])

  useEffect(() => {
    const cleanupCb = Watchdog.register({
      handler: sessionTimeoutCheck(showToast),
      frequency: 1000,
    })
    // Reset the interval if the session changes
    return () => {
      Bugsnag.setUser()
      cleanupCb()
    }
  }, [])

  useEffect(() => {
    if (!userId) {
      return
    }
    setAmplitudeUser(userId)
    Bugsnag.setUser(userId, name, displayName)
    initiateChameleon()
    setChameleonUser(userId)
  }, [userId])

  // Add session expiration handling
  useEffect(() => {
    const handleSessionExpired = (event: CustomEvent) => {
      showToast(event?.detail?.message, MessageTypes.error, 3000, {
        vertical: 'top',
        horizontal: 'center',
      })
    }

    window.addEventListener('sessionExpired', handleSessionExpired as EventListener)

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired as EventListener)
    }
  }, [showToast])

  // If the user logs in directly to the Infra page, redirect them to the region URL
  const shouldRedirectToRegionURL = useMemo(() => {
    if (loadingRegion || loadingRegionList) {
      return false
    }
    return isInfraRegion() && !isSingleRegionDeployment && !isOpenCloud()
  }, [loadingRegion, isInfraRegion, isSingleRegionDeployment, loadingRegionList])

  const isLoading =
    loadingRegion || isLoadingInitialData || shouldRedirectToRegionURL || !isUserDetailsLoaded

  return (
    <>
      <DocumentMeta title="Welcome" />
      {isLoading ? (
        <Progress
          loading
          message={
            shouldRedirectToRegionURL
              ? `Redirecting to ${regionTerm}...`
              : `Loading ${regionTerm} data...`
          }
        >
          {/* Redirect to secondary region if needed */}
          {shouldRedirectToRegionURL && <RedirectToSecondaryRegion hideDropdown={true} />}
        </Progress>
      ) : (
        <ControlPlaneServicesProvider>
          <VolumeBackendsProvider>
            <UploadProgressProvider>
              <IsoMountProgressProvider>
                <SupportBundleProgressProvider>
                  <ModalProvider>
                    <VMHAProvider>
                      {/* NB-FR-001: Shares the admin notification bar's height with the frame
                          and the bottom-fixed overlays, the enrollment and revert trays included */}
                      <NotificationBarOffsetRoot>
                        <EnrollmentFeature>
                          <RevertVmFeature>
                            {/* APP-FR-001: Banners take their height from the frame instead of
                                pushing it down, so the page never scrolls and the frame's bottom
                                (the admin notification bar) stays in view */}
                            <div className={classes.appShell}>
                              <AppBanners />
                              <PluginFrame
                                currentPluginId={currentStack}
                                features={features}
                                role={role}
                              />
                            </div>
                          </RevertVmFeature>
                        </EnrollmentFeature>
                      </NotificationBarOffsetRoot>
                    </VMHAProvider>
                  </ModalProvider>
                </SupportBundleProgressProvider>
              </IsoMountProgressProvider>
            </UploadProgressProvider>
          </VolumeBackendsProvider>
        </ControlPlaneServicesProvider>
      )}
      {/* Session Expiry Warning Modal */}
      <SessionExpiryModal />
    </>
  )
}

export default AuthContainer

const useStyles = makeStyles({
  // APP-FR-001: The window's height, shared by the banners (as tall as they need) and the frame
  // (the rest)
  appShell: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
  },
})
